// Centralized chain metadata & helpers
// Naming Conventions:
//  - label: Canonical network display name (marketing / user-friendly)
//  - short: A concise alias sometimes used in pills (NOT always the coin ticker)
//  - We intentionally do NOT overload 'short' with coin symbols when they differ from network naming (e.g. Polygon vs MATIC token).
//  - External display that needs chain id should compose: `${chainId} · ${label}`.
// Extend this map as more chains are supported.
export const CHAIN_METADATA = {
  '0x1':     { key: 'eth-mainnet',     label: 'Ethereum Mainnet', short: 'ETH',     nativeSymbol: 'ETH',   nativeDecimals: 18, explorerBaseUrl: 'https://etherscan.io' },
  '0x2105':  { key: 'base-mainnet',    label: 'Base Mainnet',      short: 'Base',    nativeSymbol: 'ETH',   nativeDecimals: 18, explorerBaseUrl: 'https://basescan.org' },
  '0x89':    { key: 'polygon-mainnet', label: 'Polygon PoS',       short: 'Polygon', nativeSymbol: 'MATIC', nativeDecimals: 18, explorerBaseUrl: 'https://polygonscan.com' },
  '0xa4b1':  { key: 'arbitrum-one',    label: 'Arbitrum One',      short: 'Arb',     nativeSymbol: 'ETH',   nativeDecimals: 18, explorerBaseUrl: 'https://arbiscan.io' },
  '0xa':     { key: 'optimism',        label: 'OP Mainnet',        short: 'OP',      nativeSymbol: 'ETH',   nativeDecimals: 18, explorerBaseUrl: 'https://optimistic.etherscan.io' },
  '0xa4ec':  { key: 'celo-mainnet',    label: 'Celo Mainnet',      short: 'Celo',    nativeSymbol: 'CELO',  nativeDecimals: 18, explorerBaseUrl: 'https://celoscan.io' },
  '0x14a34': { key: 'base-sepolia',    label: 'Base Sepolia',      short: 'Sepolia', nativeSymbol: 'ETH',   nativeDecimals: 18, explorerBaseUrl: 'https://sepolia.basescan.org' },
};

export function getChainLabel(chainId, { fallback = 'Unknown Chain', short = false } = {}) {
  const meta = CHAIN_METADATA[chainId];
  if (!meta) return fallback;
  return short && meta.short ? meta.short : meta.label;
}

// Return a combined label like `0x1 · Ethereum Mainnet`.
// If includeAlias=true and alias differs, we produce: `0x1 · ETH · Ethereum Mainnet`.
export function getChainDisplay(chainId, { fallback = 'Unknown Chain', includeAlias = false } = {}) {
  const meta = CHAIN_METADATA[chainId];
  if (!meta) return fallback;
  if (includeAlias && meta.short && meta.short !== meta.label) {
    return `${meta.short} · ${meta.label} · ${chainId}`;
  }
  return `${meta.label} · ${chainId}`;
}

export function isSupportedChain(chainId) {
  return !!CHAIN_METADATA[chainId];
}

export function listSupportedChains() {
  return Object.keys(CHAIN_METADATA);
}

export function getChainCurrencySymbol(chainId, fallback = 'ETH') {
  const meta = CHAIN_METADATA[chainId];
  return meta?.nativeSymbol || fallback;
}

/**
 * Get a full block-explorer transaction URL for a given chain and tx hash.
 * Falls back to Etherscan if chainId is unknown.
 *
 * @param {string} chainId — Hex chain ID (e.g. '0x2105')
 * @param {string} txHash  — Transaction hash
 * @returns {string} Full explorer URL
 */
export function getExplorerTxUrl(chainId, txHash) {
  const meta = CHAIN_METADATA[chainId];
  const base = meta?.explorerBaseUrl || 'https://etherscan.io';
  return `${base}/tx/${txHash}`;
}
